// Pure scrubbing helpers for Sentry `beforeSend`.
// Tested in tests/sentryScrub.test.ts under bare Node.

const SENSITIVE_KEY = /^(password|new_?password|current_?password|token|access_?token|refresh_?token|api_?key|secret|authorization|cookie|set-cookie)$/i;

// Catches inline patterns like:  password=hunter2  |  "password": "hunter2"
const SENSITIVE_INLINE = /(password|access_?token|refresh_?token|token|secret|api_?key|authorization)["'\s:=]+["']?([^"',}\s&]+)["']?/gi;

const REDACTED = '[REDACTED]';

/**
 * Redact sensitive substrings from a free-form string (e.g. an error message
 * that interpolated a request body).
 */
export function scrubString(s: string): string {
  return s.replace(SENSITIVE_INLINE, (_match, key) => `${key}=${REDACTED}`);
}

/**
 * Recursively redact sensitive keys and inline substrings from any value.
 * Mutates objects/arrays in place for efficiency — caller should pass a
 * structure they own (Sentry events are owned by beforeSend).
 */
export function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > 12) return value; // bail on extreme nesting
  if (value == null) return value;

  if (typeof value === 'string') return scrubString(value);

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      value[i] = scrubValue(value[i], depth + 1);
    }
    return value;
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      if (SENSITIVE_KEY.test(key)) {
        obj[key] = REDACTED;
      } else {
        obj[key] = scrubValue(obj[key], depth + 1);
      }
    }
    return obj;
  }

  return value;
}

const ABORT_EVENT_PATTERN = /\babort(ed|error)?\b/i;

/**
 * True when a Sentry event is just a cancelled request (AbortError) — pure
 * noise from app backgrounding / navigation, never actionable. Used in
 * `beforeSend` as a safety net for captures that bypass utils/reportError.
 */
export function isAbortEvent(event: any): boolean {
  const values = event?.exception?.values;
  if (!Array.isArray(values)) return false;
  return values.some(
    (ex) =>
      (typeof ex?.type === 'string' && ABORT_EVENT_PATTERN.test(ex.type)) ||
      (typeof ex?.value === 'string' && ABORT_EVENT_PATTERN.test(ex.value))
  );
}

export function isExpectedAuthLinkEvent(event: any): boolean {
  const values = event?.exception?.values;
  if (!Array.isArray(values)) return false;
  return values.some((ex) =>
    typeof ex?.value === 'string' &&
    /email link is invalid or has expired|token has expired or is invalid/i.test(ex.value)
  );
}

/**
 * Apply scrub to the parts of a Sentry event most likely to contain
 * accidentally-captured secrets: exceptions, breadcrumbs, request, extra,
 * contexts, and user (we also strip email from user as before).
 *
 * Untyped param signature so this module doesn't pull `@sentry/react-native`
 * into pure-Node tests.
 */
export function scrubSentryEvent(event: any): any {
  if (!event || typeof event !== 'object') return event;

  if (event.user && typeof event.user === 'object') {
    if ('email' in event.user) delete event.user.email;
    if ('ip_address' in event.user) delete event.user.ip_address;
  }

  if (event.exception?.values && Array.isArray(event.exception.values)) {
    for (const ex of event.exception.values) {
      if (ex && typeof ex.value === 'string') {
        ex.value = scrubString(ex.value);
      }
    }
  }

  if (event.breadcrumbs && Array.isArray(event.breadcrumbs)) {
    for (const bc of event.breadcrumbs) {
      if (bc?.data) bc.data = scrubValue(bc.data);
      if (typeof bc?.message === 'string') bc.message = scrubString(bc.message);
    }
  }

  if (event.request) {
    if (event.request.data !== undefined) event.request.data = scrubValue(event.request.data);
    if (event.request.headers) event.request.headers = scrubValue(event.request.headers);
    if (event.request.cookies) event.request.cookies = REDACTED;
  }

  if (event.extra) event.extra = scrubValue(event.extra);
  if (event.contexts) event.contexts = scrubValue(event.contexts);

  return event;
}
